<template>
  <NuxtLoadingIndicator :height="2" />
  <div
    id="mainApp"
    class="main-app"
    :class="{
      'main-app--in-webview': isWebViewFarmi(useRequestHeader('user-agent')),
      'main-app--no-header': route?.meta?.showAppHeader === false,
    }"
  >
    <NuxtLayout>
      <NuxtPage class="content-router" />
    </NuxtLayout>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  setPageLayout,
  useAsyncData,
  useHead,
  useNuxtData,
  useRequestHeader,
  useRoute,
  useRouter,
  watch,
} from '#imports'
import { getAppContext } from '@b2ag/micro-front-tools'
import { isWebViewFarmi } from '@b2ag/farmi-app-appro-tools'
import { useStore } from 'vuex'
import { i18n } from '@b2ag/locale'
import { currentAppBrand, isFarmi } from '@b2ag/app-brands'
import { useCategoriesSlugs } from '@b2ag/search/src/composables/useCategoriesSlugs'
import type { Store } from '@b2ag/store'
import { useCheckNewVersion } from './helpers/useCheckNewVersion'
import { initPartners } from '@b2ag/partner/src/initPartners'
import { provideCategoryDescription } from '@b2ag/marketing/src/application/composables/useCategoryDescription'
import { provideTargetMembership, useTargetMembership } from '@b2ag/membership/src/composables/useTargetMembership'
import { provideMembershipCounts } from '@b2ag/membership/src/composables/useMembershipCounts'
import { provideMembershipStore } from '@b2ag/membership/src/composables/useMembershipStore'
import { provideShoppingList } from '@b2ag/shopping-list/src/composables/useShoppingList'
import { useAddresses } from '@b2ag/address/src/composables/useAddresses'

useCheckNewVersion()

const { appFrontType } = getAppContext()

const store = useStore() as Store
const router = useRouter()
const route = useRoute()

const isUserLoaded = computed(() => store.getters.isUserLoaded)
const { fetchAddresses } = useAddresses()

setPageLayout(route.meta?.layout || getAppContext().appFrontType)

if (isFarmi) {
  const { provideAddressReactivitySettings } = await import('@b2ag/soufflet-pricer')
  const { makeAddressReactivitySettings } = await import('@/app/contexts/soufflet-pricer')

  provideAddressReactivitySettings(makeAddressReactivitySettings(store))
}

switch (appFrontType) {
  case 'agri':
    initPartners()
    provideCategoryDescription()
    break
  case 'tech':
    provideMembershipStore(store)
    provideTargetMembership(store)
    provideMembershipCounts(store)
    provideShoppingList()

    watch(
      isUserLoaded,
      async () => {
        if (isUserLoaded.value && !store.getters.targetCustomer) {
          await useTargetMembership().loadTargetMembership()
        }
        // Get partner ID and membership code from store for fetchAddresses
        const partnerId = store.getters.currentCooperativeId
        const membershipCode = store.getters.targetCustomerMembership || store.getters.currentMembershipNumber
        if (partnerId && membershipCode) {
          await fetchAddresses(partnerId, membershipCode)
        }
      },
      { immediate: true },
    )
    break
  default:
    break
}

const { fetchCategories } = useCategoriesSlugs()
await fetchCategories({ useNuxtData, useAsyncData })

// Nécessaire pour les micro-front en module federation
if (typeof window !== 'undefined') {
  window.globalStore = store
  window.parentRouter = router as any
}

useHead(() => {
  return {
    title: i18n._('Aladin, plateforme agricole d’achat de biens'),
    meta: [{ name: 'apple-mobile-web-app-title', content: currentAppBrand }],
    htmlAttrs: {
      lang: i18n.locale,
    },
  }
})
</script>

<style lang="scss">
@import './assets/global';
</style>
